import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDebounceFn } from '@vueuse/core'
import type { RouteLocationNamedRaw } from 'vue-router'
import { useProductsStore } from '@/stores'
import type { Product } from '@/types'
import { safeLocalStorage, isClient } from '@/composables/useSSR'
import { takeTransferCodes, validSearchNames } from '@/composables/useDomainSearch'

export type SearchCategory = 'navigation' | 'product' | 'domain'

export type SearchResult = {
  id: string
  title: string
  subtitle?: string
  icon: string
  category: SearchCategory
  route?: RouteLocationNamedRaw
  // Runs on selection, before `route` is followed.
  action?: () => void
}

// Static navigation item definitions. Titles/subtitles are i18n keys resolved
// at call time inside the composable (no `t` available at module scope).
type NavItemDef = {
  id: string
  titleKey: string
  subtitleKey?: string
  icon: string
  route: RouteLocationNamedRaw
}

const NAVIGATION_ITEM_DEFS: NavItemDef[] = [
  { id: 'nav-home', titleKey: 'globalSearch.nav.home', icon: 'mdi-home', route: { name: 'landingPage' } },
  { id: 'nav-kubernetes', titleKey: 'globalSearch.nav.kubernetes', subtitleKey: 'globalSearch.nav.kubernetesSub', icon: 'mdi-kubernetes', route: { name: 'kubernetes' } },
  { id: 'nav-vserver', titleKey: 'globalSearch.nav.vserver', subtitleKey: 'globalSearch.nav.vserverSub', icon: 'mdi-server-network', route: { name: 'vserver' } },
  { id: 'nav-webhosting', titleKey: 'globalSearch.nav.webhosting', subtitleKey: 'globalSearch.nav.webhostingSub', icon: 'mdi-server', route: { name: 'webhosting' } },
  { id: 'nav-domains', titleKey: 'globalSearch.nav.domains', subtitleKey: 'globalSearch.nav.domainsSub', icon: 'mdi-web', route: { name: 'domainsSearch' } },
  { id: 'nav-products', titleKey: 'globalSearch.nav.products', subtitleKey: 'globalSearch.nav.productsSub', icon: 'mdi-view-grid', route: { name: 'productsHome' } },
  { id: 'nav-cart', titleKey: 'globalSearch.nav.cart', icon: 'mdi-cart', route: { name: 'cart' } },
  { id: 'nav-login', titleKey: 'globalSearch.nav.login', icon: 'mdi-account', route: { name: 'login' } },
  { id: 'nav-register', titleKey: 'globalSearch.nav.register', icon: 'mdi-account-plus', route: { name: 'register' } },
  { id: 'nav-dashboard', titleKey: 'globalSearch.nav.dashboard', subtitleKey: 'globalSearch.nav.dashboardSub', icon: 'mdi-view-dashboard', route: { name: 'dashboard' } },
  { id: 'nav-impress', titleKey: 'globalSearch.nav.impress', icon: 'mdi-information-outline', route: { name: 'impress' } },
  { id: 'nav-privacy', titleKey: 'globalSearch.nav.privacy', icon: 'mdi-shield-lock-outline', route: { name: 'privacy' } },
  { id: 'nav-agb', titleKey: 'globalSearch.nav.agb', subtitleKey: 'globalSearch.nav.agbSub', icon: 'mdi-file-document-outline', route: { name: 'agb' } }
]

// Recent searches storage key
const RECENT_SEARCHES_KEY = 'itsh_recent_searches'
const MAX_RECENT_SEARCHES = 5

// Global singleton state (shared across all component instances)
const query = ref('')
const isOpen = ref(false)
const isLoading = ref(false)
const selectedIndex = ref(-1)
const recentSearches = ref<string[]>([])

// Basic domain pattern check (contains dot or common TLD keywords)
function looksLikeDomain(q: string): boolean {
  return q.includes('.') || /\.(de|com|net|org|eu|io|dev|app|shop|online)$/i.test(q)
}

// A domain query is kept as its valid names: a transfer code typed with it
// must not reach localStorage.
function recentEntry(searchQuery: string): string {
  return looksLikeDomain(searchQuery) ? validSearchNames(searchQuery).join('\n') : searchQuery
}

function storeRecentSearches(): void {
  const storage = safeLocalStorage()
  if (storage) {
    try {
      storage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(recentSearches.value))
    } catch {
      // Ignore storage errors
    }
  }
}

// Load recent searches from localStorage
function loadRecentSearches(): void {
  const storage = safeLocalStorage()
  if (!storage) return

  try {
    const stored = storage.getItem(RECENT_SEARCHES_KEY)
    if (stored) {
      // Entries saved before codes were cut may still carry one.
      recentSearches.value = (JSON.parse(stored) as string[])
        .map(recentEntry)
        .filter((s, i, all) => s && all.findIndex(o => o.toLowerCase() === s.toLowerCase()) === i)
        .slice(0, MAX_RECENT_SEARCHES)
      if (JSON.stringify(recentSearches.value) !== stored) storeRecentSearches()
    }
  } catch {
    clearRecentSearches()
  }
}

// Save a search query to recent searches
function saveRecentSearch(searchQuery: string): void {
  const entry = recentEntry(searchQuery.trim())
  if (!entry) return

  // Remove duplicate if exists
  const filtered = recentSearches.value.filter(s => s.toLowerCase() !== entry.toLowerCase())
  // Newest first, capped
  recentSearches.value = [entry, ...filtered].slice(0, MAX_RECENT_SEARCHES)
  storeRecentSearches()
}

// Clear recent searches
function clearRecentSearches(): void {
  recentSearches.value = []
  const storage = safeLocalStorage()
  if (storage) {
    try {
      storage.removeItem(RECENT_SEARCHES_KEY)
    } catch {
      // Ignore storage errors
    }
  }
}

// Open search
function open(): void {
  isOpen.value = true
  selectedIndex.value = -1
  loadRecentSearches()
}

// Close search
function close(): void {
  isOpen.value = false
  query.value = ''
  selectedIndex.value = -1
}

// Toggle search
function toggle(): void {
  if (isOpen.value) {
    close()
  } else {
    open()
  }
}

// Initialize recent searches on module load (client only)
if (isClient) {
  loadRecentSearches()
}

export function useGlobalSearch() {
  const productsStore = useProductsStore()
  const { t, locale } = useI18n()

  // Localized navigation items (re-resolved when the locale changes)
  const navigationItems = computed<SearchResult[]>(() =>
    NAVIGATION_ITEM_DEFS.map((def) => ({
      id: def.id,
      title: t(def.titleKey),
      subtitle: def.subtitleKey ? t(def.subtitleKey) : undefined,
      icon: def.icon,
      category: 'navigation' as const,
      // Public routes now sit under /:locale, so resolving one without the
      // param throws. Routes that do not declare it ignore the surplus, so
      // adding it unconditionally is safe and keeps this list declarative.
      route: { ...def.route, params: { locale: locale.value } }
    }))
  )

  // Filter navigation items
  const filteredNavigation = computed<SearchResult[]>(() => {
    const q = query.value.trim().toLowerCase()
    if (!q) return navigationItems.value.slice(0, 4) // Show top 4 when empty

    return navigationItems.value.filter(item =>
      item.title.toLowerCase().includes(q) ||
      item.subtitle?.toLowerCase().includes(q)
    )
  })

  // Filter products
  const filteredProducts = computed<SearchResult[]>(() => {
    const q = query.value.trim().toLowerCase()
    if (!q) return []

    const products = productsStore.getProducts || []
    return products
      .filter((product: Product) =>
        product.name.toLowerCase().includes(q) ||
        product.description?.toLowerCase().includes(q)
      )
      .slice(0, 5) // Limit to 5 results
      .map((product: Product): SearchResult => ({
        id: `product-${product.slug || product.name}`,
        title: product.name,
        subtitle: product.description?.slice(0, 60) + (product.description && product.description.length > 60 ? '...' : ''),
        icon: getProductIcon(product),
        category: 'product',
        route: getProductRoute(product)
      }))
  })

  // Domain search result (if query looks like domain)
  const domainSearchResult = computed<SearchResult | null>(() => {
    const q = query.value.trim()
    const names = validSearchNames(q)
    if (!names.length) return null
    return {
      id: 'domain-search',
      title: t('globalSearch.domainResult.title', { query: names.join(', ') }),
      subtitle: t('globalSearch.domainResult.subtitle'),
      icon: 'mdi-web-check',
      category: 'domain',
      // As from every other entry point, the URL carries only the names and
      // the codes are stashed for the search page once the result is chosen.
      route: { name: 'domainsSearch', query: { query: names.join('\n').toLowerCase() } },
      action: () => takeTransferCodes(q)
    }
  })

  // All results combined
  const results = computed<SearchResult[]>(() => {
    const allResults: SearchResult[] = []

    // Add domain search if applicable
    if (domainSearchResult.value) {
      allResults.push(domainSearchResult.value)
    }

    // Add navigation results
    allResults.push(...filteredNavigation.value)

    // Add product results
    allResults.push(...filteredProducts.value)

    return allResults
  })

  // Group results by category
  const groupedResults = computed(() => {
    const groups: { category: SearchCategory; label: string; items: SearchResult[] }[] = []

    const domains = results.value.filter(r => r.category === 'domain')
    const navigation = results.value.filter(r => r.category === 'navigation')
    const products = results.value.filter(r => r.category === 'product')

    if (domains.length > 0) {
      groups.push({ category: 'domain', label: t('globalSearch.groups.domain'), items: domains })
    }
    if (navigation.length > 0) {
      groups.push({ category: 'navigation', label: t('globalSearch.groups.navigation'), items: navigation })
    }
    if (products.length > 0) {
      groups.push({ category: 'product', label: t('globalSearch.groups.product'), items: products })
    }

    return groups
  })

  // Get product icon based on type/features
  function getProductIcon(product: Product): string {
    const features = product.features as Record<string, unknown> | undefined
    const type = features?.type as string | undefined

    if (type === 'kubernetes' || product.name.toLowerCase().includes('kubernetes')) {
      return 'mdi-kubernetes'
    }
    if (type === 'hosting' || product.name.toLowerCase().includes('hosting')) {
      return 'mdi-server'
    }
    if (type === 'domain' || product.name.toLowerCase().includes('domain')) {
      return 'mdi-web'
    }
    return 'mdi-package-variant'
  }

  // Get product route
  function getProductRoute(product: Product): RouteLocationNamedRaw {
    const features = product.features as Record<string, unknown> | undefined
    const type = features?.type as string | undefined

    if (type === 'kubernetes') {
      return { name: 'kubernetes' }
    }
    if (type === 'hosting') {
      return { name: 'webhosting' }
    }
    return { name: 'productsHome' }
  }

  // Keyboard navigation
  function handleKeyDown(event: KeyboardEvent): void {
    const totalResults = results.value.length

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        selectedIndex.value = selectedIndex.value < totalResults - 1 ? selectedIndex.value + 1 : 0
        break
      case 'ArrowUp':
        event.preventDefault()
        selectedIndex.value = selectedIndex.value > 0 ? selectedIndex.value - 1 : totalResults - 1
        break
      case 'Escape':
        event.preventDefault()
        close()
        break
    }
  }

  // Get selected result
  function getSelectedResult(): SearchResult | null {
    if (selectedIndex.value >= 0 && selectedIndex.value < results.value.length) {
      return results.value[selectedIndex.value]!
    }
    return results.value[0] ?? null
  }

  // Debounced search (for future API integration)
  const debouncedSearch = useDebounceFn(() => {
    // Placeholder for future API-based search
    isLoading.value = false
  }, 300)

  // Watch query changes
  watch(query, () => {
    isLoading.value = true
    selectedIndex.value = -1
    debouncedSearch()
  })

  return {
    query,
    isOpen,
    isLoading,
    selectedIndex,
    results,
    groupedResults,
    recentSearches,
    open,
    close,
    toggle,
    handleKeyDown,
    getSelectedResult,
    saveRecentSearch,
    clearRecentSearches
  }
}
